/**
 * ObjectDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel with 3 tabs:
 * Config, Refine, Variants
 *
 * Width: 400px. Image aspect: 16:9 (aspect-video).
 */

import React, { memo, useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Position, NodeProps, useReactFlow, Handle } from 'reactflow'
import { getAssetUrl, getCanvasPreviewUrl } from '../../../../api/useAssetUrl'
import { AnnotationEditorOverlay, useImageAnnotation } from '../shared/annotate'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { useExecuteNodeAction } from '../../../../hooks/useExecuteNodeAction'
import { generateModeFor } from '../../../../analytics/nodeAnalytics'
import { useCanvasOptional } from '../../CanvasContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { getImageModelAlias } from '../../../../constants/modelAliases'
import { dataStatus,dataStrOpt, type ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  QuickInfoBar,
  OBJECT_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
  IMAGE_MODEL_OPTIONS,
  RefineTabContent,
  VariantsTabContent,
  computeVariantPanelLayout,
  useDesignerVariantSelection,
  useVariantImageDownload,
  useNodeActions,
  useSaveToLibrary,
  ShimmerPlaceholder,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  createHandlesFromSchema,
  generatedImageHistoryToJson,
  generatedImagePath,
  getDesignerMainImage,
  getPickedIndex,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  FlatIdleNodeCard,
  useAssetNodeGeometryTrace,
  type HistoryEntry,
  computeBorderStyle,
} from '../shared'
import { useFeedbackMode, type RefineRunOptions } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { resolveIdentityBorderState, useNodeIdentityBorderTrace } from '../shared/useNodeIdentityBorderTrace'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { cn } from '../../../../lib/utils'
import { NODE_CARD_RADIUS_CLASS, NODE_CARD_INNER_RADIUS_CLASS } from '../shared/constants'
import { BookmarkPlus, Check, Loader2, Maximize2 } from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from '../shared/MetaLoadingSurface'
import {
  NODE_SCHEMAS_BY_TYPE,
  type ReactFlowCanvasNodeDataByType,
} from '../../../../generated/nodeDataTypes'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

const objectDesignerSchema = NODE_SCHEMAS_BY_TYPE.objectDesigner
type ObjectDesignerNodeData = ReactFlowCanvasNodeDataByType['objectDesigner']

// Card width (px) and the fixed variant-grid aspect (object cards are 16:9).
// The object designer agent renders 16:9 images, so the preview must be 16:9
// too or object-cover crops the render. Shared by all asset designer nodes --
// keep in sync with schemas/nodes/object_designer.json.
const NODE_WIDTH = 400
const VARIANTS_ASPECT = '16/9'
const variantsPanelLog = createNadDebug('dw:variants-panel')
const descriptionStateDebug = createDebug('dw:canvas:designer-description')
const descriptionStateLog = createNadDebug('dw:canvas:designer-description')

const HANDLES = createHandlesFromSchema(objectDesignerSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  outputs: {
    out_name: { position: Position.Right, style: { top: '35%' } },
    out_metadata: { position: Position.Right },
  },
})

const agentColor = AGENT_COLORS.object

const ObjectDesignerNode = memo(function ObjectDesignerNode(
  props: NodeProps<ObjectDesignerNodeData>
) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { t } = useTranslation('canvas')
  const { id, data, selected, dragging } = props
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const projectId = useProjectId()

  const { setNodes, getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'objectDesigner', data)

  const in_name = node?.get('in_name') ?? ''
  const in_style = node?.get('in_style')
  const in_model = node?.get('in_model') ?? 'gemini-3.1-flash-image-preview'
  const in_description = node?.get('in_description') ?? ''
  const out_image = node?.get('out_image')
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat } = useNodeActions({ nodeId: id })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, in_name || t('objectDesignerNode.untitledObject'))
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId: id, selected })
  const setHoveredNodeId = useSetHoveredNodeId()
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const [isHovered, setIsHovered] = useState(false)
  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)

  // Image URL resolution
  const mainImg = getDesignerMainImage(out_image)
  let imagePath: string | undefined
  const mainObjHistory = mainImg?.history ?? []
  if (mainObjHistory.length) {
    const idx = getPickedIndex(mainImg)
    imagePath = generatedImagePath(mainObjHistory[idx])
  }
  const cacheBust = cacheKey

  // Footer config summary. `getImageModelAlias` misses only for a model ID the
  // capability catalog does not know (a retired or BYOM value persisted on an
  // old node), so the option list and then a generic label back it up.
  const configSummary =
    getImageModelAlias(in_model)
    ?? IMAGE_MODEL_OPTIONS.find((o) => o.value === in_model)?.label
    ?? 'Model'
  const imageUrl =
    imagePath && projectId ? getCanvasPreviewUrl(projectId, imagePath, cacheBust) : null
  const fullImageUrl = imagePath && projectId ? getAssetUrl(projectId, imagePath, 0) : null

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showLoading = isRunning || isPendingExecution || isPendingCost
  const loadingMessage = isRunning
    ? (progressMessage || (imageUrl
      ? t('objectDesignerNode.refiningObject')
      : in_name
        ? t('objectDesignerNode.creatingNamed', { name: in_name })
        : t('objectDesignerNode.creating')))
    : isPendingCost
      ? t('designerNodeShell.waitingCostApproval')
      : t('designerNodeShell.queuedWaiting')
  const metaLoading = useMemo<MetaLoadingDescriptor | undefined>(() => {
    if (!showLoading) return undefined
    return {
      message: loadingMessage,
      nodeType: 'objectDesigner',
      loadingState: isPendingCost
        ? 'pending-cost'
        : isPendingExecution
          ? 'pending-execution'
          : 'running',
    }
  }, [isPendingCost, isPendingExecution, loadingMessage, showLoading])
  // NAD trace: prove this card's width matches its schema and that the preview
  // is 16:9 (post 1592855802471717). Filter with
  // `nad trace --feature asset-node-geometry`.
  useAssetNodeGeometryTrace({
    nodeId: id,
    nodeType: 'objectDesigner',
    componentWidth: NODE_WIDTH,
    schemaWidth: objectDesignerSchema.layout['*'].width,
    imageAspectClass: 'aspect-video',
    variantsAspect: VARIANTS_ASPECT,
  })

  useEffect(() => {
    descriptionStateLog({
      event: 'designer-description:render-state',
      trace_id: `designer-description:${id}:${status}`,
      node_id: id,
      node_type: 'objectDesigner',
      status,
      description_state: isRunning ? 'shimmer' : 'textarea',
      debug_enabled: descriptionStateDebug.enabled,
    })
  }, [id, isRunning, status])

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  // Variants now open as a dedicated, tab-less panel via the quick-info bar
  // (post 1533007285123236) — filter it out of the config-mode tab bar.
  const tabs = useMemo(() => {
    return OBJECT_TABS.filter((t) => t.id !== 'variants')
  }, [])
  const variantsMode = activeTab === 'variants'
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isModalOpen)
  const selectSingleNode = useSelectSingleCanvasNode()

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
      variantsPanelLog({
        event: 'bottom-panel:open',
        trace_id: `variants-panel:${id}`,
        node_id: id,
        node_type: 'objectDesigner',
        mode: tab === 'variants' ? 'variants' : 'config',
        active_tab: tab,
      })
    },
    [id, selectSingleNode]
  )

  // History state
  const mainImageHistory = useMemo(
    () => {
      void cacheKey
      return getDesignerMainImage(out_image)
    },
    [out_image, cacheKey]
  )
  const historyEntries = useMemo(
    (): HistoryEntry[] => {
      void cacheKey
      return generatedImageHistoryToJson(mainImageHistory?.history)
    },
    [mainImageHistory, cacheKey]
  )
  const pickedIndex = getPickedIndex(mainImageHistory)
  const handleVariantDownload = useVariantImageDownload({ nodeId: id, history: historyEntries })
  const batchPending = isRunning ? (node?.get('batch_pending') ?? 0) : 0

  // Dedicated Variants panel layout: tiles match the preview width; panel sized
  // for the grid (2 cols for 16:9) without scroll, grows then scrolls beyond.
  const variantLayout = useMemo(
    () =>
      computeVariantPanelLayout(
        VARIANTS_ASPECT,
        NODE_WIDTH,
        historyEntries.length + batchPending,
      ),
    [historyEntries.length, batchPending],
  )

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    nodeType: 'objectDesigner',
    status,
  })

  // Shared variant selection (writes images.main.picked_index and derives out_image)
  const handleHistorySelect = useDesignerVariantSelection({
    nodeId: id,
    nodeType: 'objectDesigner',
    historyEntries,
    outputFieldName: 'out_image',
  })

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      node?.writeInDescription(newDesc)
    },
    [id, setNodes, node]
  )

  // Config generate handler — useExecuteNodeAction adds 1s cooldown
  // and routes insufficient_sparks 402 to the workspace modal.
  const { runExecute, isLocked: generateLocked, cancelExecution, isCancelling } = useExecuteNodeAction({
    nodeType: 'object_designer',
    projectId,
    nodeId: id,
    isRunning,
  })
  const cancelButton = isRunning ? (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        cancelExecution()
      }}
      disabled={isCancelling}
      className="nodrag mt-3 flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50"
      data-testid="cancel-node-button"
      title={t('objectDesignerNode.stop')}
    >
      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="6" width="12" height="12" rx="1" />
      </svg>
      <span className="text-xxs font-medium">{t('objectDesignerNode.stop')}</span>
    </button>
  ) : null
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      runExecute(retryCount, { analytics: { tab: 'config', generationMode: generateModeFor(historyEntries.length > 0) } })
    },
    [runExecute, historyEntries.length],
  )

  // Refine handler
  const onRefineSubmit = useCallback(
    (retryCount?: number, options?: RefineRunOptions) => {
      handleRefine(retryCount, options)
    },
    [handleRefine],
  )

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      node?.writeInStyle(styleId)
    },
    [id, setNodes, node]
  )

  // Model change handler
  const handleModelChange = useCallback(
    (model: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_model: model } } : n
        )
      )
      node?.writeInModel(model)
    },
    [id, setNodes, node]
  )

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [id, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      node?.writeInDescription(value)
    },
    [id, setNodes, node]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      node?.writeInName(newName)
    },
    [id, setNodes, node],
  )

  // Download handler — standardized filename via shared helper.
  const onDownload = useCallback(() => {
    if (!imagePath) return
      const { filename } = buildAssetDownloadFilename({
        canvasNodes: getNodes(),
        canvasEdges: edges,
        nodeId: id,
        assetPath: imagePath,
        mediaType: 'image',
      variantHistory: historyEntries,
      pickedIndex,
    })
    handleDownload(imagePath, filename)
  }, [imagePath, handleDownload, getNodes, edges, id, historyEntries, pickedIndex])
  useRegisterNodeContextAction(id, 'download', onDownload, Boolean(imagePath))

  // RFC-1115: annotate the full-resolution asset, not the thumbnail —

  // the bake reads the original.

  const annotate = useImageAnnotation({

    nodeId: id,

    feNodeType: 'objectDesigner',

    imagePath,

    imageUrl: fullImageUrl,

    projectId,

  })


  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'addToChat',
        label: t('objectDesignerNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      annotate.toolbarAction,
      {
        key: 'duplicate',
        label: t('objectDesignerNode.duplicate'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('objectDesignerNode.delete'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [duplicateMenuItems, handleDelete, handleAddToChat, handleSaveToLibrary, saveState, saveLabel, t, annotate.toolbarAction]
  )

  // Border color state machine (shared) — selected is white like every other
  // designer card, not the object's agent color.
  const computedBorderStyle = computeBorderStyle(!!selected, status, isHovered, agentColor)

  // Card side of the shared identity-border trace (post 1579940090429955).
  // This node computes its border inline rather than through DesignerNodeShell,
  // so without this the trace would only ever cover the character node.
  useNodeIdentityBorderTrace({
    nodeId: id,
    nodeType: 'objectDesigner',
    surface: 'card',
    agentColor,
    appliedBorderColor: computedBorderStyle.borderColor,
    borderState: resolveIdentityBorderState(!!selected, status, isHovered),
  })

  // Panel visibility
  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  const shouldRenderFlatNode = lightweightNodeChrome && !selected && !dragging &&
    !isHoveringLightweightNodeChrome && !isModalOpen && !annotate.isOpen

  if (shouldRenderFlatNode) {
    return (
      <div
        data-testid="object-designer-lightweight-host"
        className="w-fit"
        onPointerEnter={() => { setIsHoveringLightweightNodeChrome(true); setIsHovered(true); setHoveredNodeId(id) }}
        onPointerLeave={() => { setIsHoveringLightweightNodeChrome(false); setIsHovered(false); setHoveredNodeId(null) }}
      >
        <FlatIdleNodeCard
          nodeId={id}
          testId="object-designer-flat-node"
          label={in_name}
          fallbackLabel={t('objectDesignerNode.altObject')}
          width={NODE_WIDTH}
          status={status}
          error={error ? sanitizeNodeError(error, t, true) : undefined}
          progressMessage={loadingMessage}
          metaLoading={metaLoading}
          handles={HANDLES}
          mediaKind={imageUrl ? 'image' : 'none'}
          mediaUrl={imageUrl}
          outputVariantIndex={pickedIndex}
          aspectClassName="aspect-video"
          placeholderLabel={t('objectDesignerNode.noObjectYet')}
          compactHandles
        />
      </div>
    )
  }

  return (
    <>
      <div
        className="relative"
        onPointerEnter={lightweightNodeChrome ? () => { setIsHoveringLightweightNodeChrome(true); setIsHovered(true); setHoveredNodeId(id) } : undefined}
        onPointerLeave={lightweightNodeChrome ? () => { setIsHoveringLightweightNodeChrome(false); setIsHovered(false); setHoveredNodeId(null) } : undefined}
      >
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* RFC-1115 annotation editor. Mounted at the NODE ROOT, not inside the
            image container: that container clips (`overflow: hidden`), which cuts
            off the tool palette — wider than the node card — and leaves its
            buttons unclickable behind the ReactFlow pane. */}
        {annotate.isOpen && fullImageUrl && (
          <AnnotationEditorOverlay
            imageUrl={fullImageUrl}
            altText={t('objectDesignerNode.altObject')}
            traceId={annotate.traceId}
            nodeId={id}
            isSaving={annotate.isSaving}
            onCancel={annotate.close}
            onSave={annotate.save}
          />
        )}

        {/* Floating label above the card */}
        <FloatingNodeLabel name={in_name} fallback={t('objectDesignerNode.untitledObject')} typeIcon={NODE_TYPE_ICON.objectDesigner} onRename={handleRename} />

        {/* Main Card */}
        <div
          data-testid="object-designer-node"
          className={cn(
            'canvas-node-card-contained relative border bg-surface-0 transition-all duration-200 overflow-hidden cursor-pointer',
            NODE_CARD_RADIUS_CLASS,
            status === 'running' && 'animate-node-shimmer'
          )}
          style={{ width: nodePresentationWidth(NODE_WIDTH), ...computedBorderStyle }}
          onMouseEnter={() => {
            setIsHovered(true)
            setHoveredNodeId(id)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
            setHoveredNodeId(null)
          }}
        >
          {/* ReactFlow Handles */}
          {handlesVisible && HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Image Area -- 16:9 aspect ratio */}
          <div className="relative group p-1.5">
            {/* RFC-1115 annotation editor. `inset-1.5` matches the container's
                padding so the editable surface lines up with the image. */}

            {imageUrl && (
              <img
                src={imageUrl}
                alt={t('objectDesignerNode.altObject')}
                aria-hidden={showLoading || undefined}
                decoding="async"
                className={cn(
                  'w-full aspect-video object-cover block',
                  NODE_CARD_INNER_RADIUS_CLASS,
                  showLoading && 'pointer-events-none'
                )}
              />
            )}

            {metaLoading && imageUrl && (
              <MetaLoadingSurface
                {...metaLoading}
                projectId={projectId}
                nodeId={id}
                presentation="full"
                className={NODE_CARD_INNER_RADIUS_CLASS}
                style={{ inset: '0.375rem' }}
              >
                {cancelButton}
              </MetaLoadingSurface>
            )}

            {!imageUrl && metaLoading && (
              <div className={`relative w-full aspect-video bg-surface-1/50 ${NODE_CARD_INNER_RADIUS_CLASS}`}>
                <MetaLoadingSurface
                  {...metaLoading}
                  projectId={projectId}
                  nodeId={id}
                  presentation="full"
                  className={NODE_CARD_INNER_RADIUS_CLASS}
                >
                  {cancelButton}
                </MetaLoadingSurface>
              </div>
            )}

            {!imageUrl && !showLoading && (
              <div className={`w-full aspect-video bg-surface-1/50 ${NODE_CARD_INNER_RADIUS_CLASS} flex flex-col items-center justify-center`}>
                <svg
                  className="w-8 h-8 text-text-muted/20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <span className="text-xxs text-text-muted/40 mt-1">{t('objectDesignerNode.noObjectYet')}</span>
              </div>
            )}

            {imageUrl && !showLoading && (
              <div className="absolute top-[14px] right-[14px] flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsModalOpen(true)
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title={t('objectDesignerNode.expand')}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title={t('objectDesignerNode.download')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Status overlay — floats over the bottom of the image box (inset
                to match the container padding) instead of adding a footer, so a
                failure never changes the card's outline size (post
                1634117708345526). */}
            {status === 'error' && error && (
              <NodeStatusMessage
                message={sanitizeNodeError(error, t, true)}
                testId="designer-card-error"
                className="inset-x-1.5 bottom-1.5 rounded-b-sm"
              />
            )}
          </div>

          {/* Content Area */}
          <div className="pt-2 px-6 pb-6">
            <span className="text-xs font-medium" style={{ color: agentColor.active }}>
              {t('objectDesignerNode.altObject')}
            </span>

            {/* Description -- editable */}
            <div className="mt-2 h-[60px]">
              {isRunning ? (
                <div className="space-y-1.5">
                  <ShimmerPlaceholder width="100%" height={14} />
                  <ShimmerPlaceholder width="80%" height={14} />
                </div>
              ) : (
                <textarea
                  data-testid="object-description-input"
                  aria-label={t('objectDesignerNode.objectDescription')}
                  value={in_description || ''}
                  placeholder={t('objectDesignerNode.describeObject')}
                  onChange={handleDescriptionChange}
                  className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
                  rows={3}
                />
              )}
            </div>
          </div>

          {/* Quick Info Bar -- 2 columns: Config | Variants */}
          <QuickInfoBar
            presentation="designer"
            configSummary={configSummary}
            references={currentRefs}
            projectId={projectId}
            cacheBust={cacheBust}
            variantCount={historyEntries.length}
            accentColor={agentColor.active}
            logSource="object-designer"
            onOpenConfig={() => openPanelToTab('config')}
            onOpenVariants={() => openPanelToTab('variants')}
          />
        </div>

        {/* Unified Bottom Panel */}
        <UnifiedBottomPanel
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
          hideTabBar={variantsMode}
          // Every tab is as wide as the Variants panel. Config and Refine
          // previously fell back to `calc(100% + 120px)` — 520px against a
          // 400px node, versus 832px for Variants — which left them cramped
          // and made switching tabs resize the panel under the cursor.
          // Height still varies: the Variants height is derived from the tile
          // count and collapses to 150px when empty, which would make Config
          // SMALLER, so the other tabs keep the fixed default.
          minWidth={variantLayout.minWidth}
          contentHeight={variantsMode ? variantLayout.contentHeight : undefined}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="object"
                  prompt={in_description}
                  onPromptChange={handlePromptChange}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={handleStyleChange}
                  model={in_model}
                  onModelChange={handleModelChange}
                  isRunning={isRunning || generateLocked}
                  onGenerate={handleConfigGenerate}
                  onCancel={cancelExecution}
                  isCancelling={isCancelling}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  nodeId={id}
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={onRefineSubmit}
                  isRunning={isRunning}
                  nodeType="objectDesigner"
                  selectedStyleId={in_style}
                  onStyleChange={handleStyleChange}
                  onModelChange={handleModelChange}
                  model={in_model}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  targetBackendType="object_designer"
annotationSourcePath={imagePath}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  onDownloadVariant={handleVariantDownload}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  nodeId={id}
                  nodeType="objectDesigner"
                  agentColor={agentColor}
                  aspectRatio={VARIANTS_ASPECT}
                  maxWidth={NODE_WIDTH}
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt={t('objectDesignerNode.altFullSize')}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

ObjectDesignerNode.displayName = 'ObjectDesignerNode'
export default ObjectDesignerNode
import { useNodePresentationWidth } from '../../NodePresentationContext'
